import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  X,
  Play,
  Square,
  AlertTriangle,
  Music2,
  Clock,
  ShieldAlert,
  Trophy,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fmtMinSec = (s) => {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const STATUS_LABEL = {
  in_progress: "Live Session",
  completed: "Completed",
};

const labelForStatus = (status) => STATUS_LABEL[status] || status;

// Visual state of a single set row → drives borders/backgrounds.
const rowVariant = (s, isActive) => {
  if (s.locked) return "border-[#C6FF00]/40 bg-[#C6FF00]/5";
  if (s.confirm_status === "disputed") return "border-red-500/40 bg-red-500/5";
  if (isActive) return "border-white/20 bg-[#111]";
  return "border-white/10 bg-[#0F0F0F] opacity-80";
};

// Which controls a given set should render based on whose turn it is.
// Returns one of: 'locked' | 'mark' | 'confirm' | 'waiting' | 'pending'.
const setMode = ({ s, status, isActive, myMark, partnerMark }) => {
  if (s.locked) return "locked";
  if (status === "in_progress" && isActive && !s.marked_by) return "mark";
  if (status === "in_progress" && partnerMark && s.confirm_status === "pending") return "confirm";
  if (myMark && s.confirm_status === "pending") return "waiting";
  return "pending";
};

const SetTestLive = () => {
  const { setTestId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [st, setSt] = useState(null);
  const [tick, setTick] = useState(0);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/set-tests/${setTestId}`);
      setSt(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
      if (err?.response?.status === 404) nav("/app/schedule");
    }
  }, [setTestId, nav]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 3500);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(t);
    };
  }, [load]);

  const action = async (path, body) => {
    setActing(true);
    try {
      await api.post(`/set-tests/${setTestId}${path}`, body);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Action failed.");
    } finally {
      setActing(false);
    }
  };

  const sos = async () => {
    try {
      await api.post("/safety/sos", { set_test_id: setTestId });
      toast.success("Emergency contact notified.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "SOS failed.");
    }
  };

  if (!st) {
    return <div className="text-center py-20 text-neutral-500">Loading Set Test…</div>;
  }

  const isProposer = st.is_proposer;
  const myId = user.id;
  const partner = st.other_user;
  const status = st.status;

  // Time progress
  const startedAt = st.started_at ? new Date(st.started_at) : null;
  const now = new Date();
  const elapsedSec = startedAt && status === "in_progress" ? (now - startedAt) / 1000 + tick * 0 : st.total_seconds;

  // Find first unlocked plan item — that's the active set
  const activeIdx = st.sets.findIndex((s) => !s.locked);
  const activeSet = activeIdx >= 0 ? st.plan[activeIdx] : null;
  const lockedCount = st.sets.filter((s) => s.locked).length;

  return (
    <div className="flex flex-col gap-6" data-testid="set-test-live">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="text-neutral-400 hover:text-white p-1"
          aria-label="Back"
          data-testid="set-test-back-btn"
        >
          <ArrowLeft size={20} />
        </button>
        <img
          src={partner?.photo_url}
          alt={partner?.name}
          className="w-10 h-10 rounded-full object-cover border border-white/10"
        />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6FF00] font-semibold">
            {labelForStatus(status)}
          </p>
          <h1 className="font-heading text-2xl leading-none truncate">{st.title}</h1>
        </div>
        {status === "in_progress" && (
          <button
            onClick={sos}
            data-testid="set-test-sos-btn"
            className="px-3 py-2 rounded-lg bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500/25 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
          >
            <ShieldAlert size={14} /> SOS
          </button>
        )}
      </div>

      {/* Live banner */}
      {status === "in_progress" && (
        <div className="rounded-2xl border border-[#C6FF00]/30 bg-[#C6FF00]/5 p-4 flex items-center justify-between" data-testid="set-test-live-banner">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#C6FF00] animate-pulse" />
            <div>
              <div className="font-heading text-lg leading-none">LIVE SESSION</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mt-1">
                Sets {lockedCount}/{st.plan.length} · {fmtMinSec(elapsedSec)} elapsed
              </div>
            </div>
          </div>
          <Clock className="text-[#C6FF00]" size={18} />
        </div>
      )}

      {/* Pass/Fail summary */}
      {status === "completed" && (
        <div
          data-testid="set-test-result"
          className={`rounded-2xl border p-6 ${
            st.pass_fail === "pass"
              ? "border-[#C6FF00]/40 bg-[#C6FF00]/10"
              : "border-red-500/40 bg-red-500/10"
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <Trophy
              size={22}
              className={st.pass_fail === "pass" ? "text-[#C6FF00]" : "text-red-400"}
            />
            <h2
              className={`font-heading text-3xl ${
                st.pass_fail === "pass" ? "text-[#C6FF00]" : "text-red-400"
              }`}
            >
              {st.pass_fail === "pass" ? "Set Test Passed" : "Set Test Failed"}
            </h2>
          </div>
          <div className="text-sm text-neutral-300">
            Sets locked: <span className="font-bold text-white">{lockedCount}/{st.plan.length}</span> ·
            Time: <span className="font-bold text-white">{fmtMinSec(st.total_seconds)}</span>
          </div>
          {st.pass_fail === "fail" && (
            <p className="text-xs text-neutral-400 mt-2">
              Need ≥ 3/4 sets locked & ≥ 15 minutes to pass. You'll bounce back next session.
            </p>
          )}
        </div>
      )}

      {/* Plan list */}
      <div className="space-y-3" data-testid="set-test-plan">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold">
            Workout Plan
          </span>
          {activeSet && (
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#C6FF00] font-semibold">
              Current · {activeSet.label}
            </span>
          )}
        </div>
        {st.plan.map((item, i) => {
          const s = st.sets[i];
          const isActive = i === activeIdx && status === "in_progress";
          const myMark = s.marked_by === myId;
          const partnerMark = s.marked_by && s.marked_by !== myId;
          const mode = setMode({ s, status, isActive, myMark, partnerMark });
          return (
            <div
              key={s.set_index ?? i}
              data-testid={`set-row-${i}`}
              className={`rounded-2xl border p-4 ${rowVariant(s, isActive)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`font-heading text-lg leading-none ${
                        s.locked ? "text-[#C6FF00]" : "text-white"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-heading text-xl leading-none">{item.label}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                    {item.kind === "reps" ? `${item.target_count} reps` : `${Math.round((item.target_seconds || 0) / 60)} min`}
                  </div>

                  {s.confirm_status === "disputed" && (
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-red-400 flex items-center gap-1">
                      <AlertTriangle size={11} /> Set disagreement — resolve together
                    </div>
                  )}
                </div>

                {/* Status / Actions */}
                <div className="flex flex-col items-end gap-1.5 min-w-[110px]">
                  <SetRowControls
                    mode={mode}
                    index={i}
                    s={s}
                    acting={acting}
                    onMark={(setStatus) => action("/mark", { set_index: i, status: setStatus })}
                    onConfirm={(confirmStatus) =>
                      action("/confirm", { set_index: i, status: confirmStatus })
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Playlist embed */}
      {st.playlist_url && (
        <div className="rounded-2xl border border-white/10 bg-[#111] p-4" data-testid="set-test-playlist">
          <div className="flex items-center gap-2 mb-3">
            <Music2 className="text-[#C6FF00]" size={14} />
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold">
              Workout Playlist
            </span>
          </div>
          <a
            href={st.playlist_url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#C6FF00] hover:underline truncate block"
          >
            {st.playlist_url}
          </a>
        </div>
      )}

      {/* Bottom actions */}
      {status === "accepted" && (
        <button
          onClick={() => action("/start")}
          disabled={acting}
          data-testid="set-test-start-btn"
          className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md hover:scale-[1.02] transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          <Play size={14} fill="currentColor" /> Start Live Session
        </button>
      )}
      {status === "in_progress" && (
        <button
          onClick={() => action("/complete")}
          disabled={acting}
          data-testid="set-test-complete-btn"
          className="bg-white text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md hover:scale-[1.02] transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          <Square size={12} fill="currentColor" /> End Session
        </button>
      )}
      {status === "proposed" && !isProposer && (
        <div className="flex gap-3">
          <button
            onClick={() => action("/respond", { action: "decline" })}
            disabled={acting}
            data-testid="set-test-decline-btn"
            className="flex-1 px-6 py-4 rounded-md border-2 border-white/15 text-neutral-300 text-xs uppercase tracking-[0.25em] font-bold inline-flex items-center justify-center gap-2"
          >
            <X size={14} /> Decline
          </button>
          <button
            onClick={() => action("/respond", { action: "accept" })}
            disabled={acting}
            data-testid="set-test-accept-btn"
            className="flex-1 bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md inline-flex items-center justify-center gap-2"
          >
            <Check size={14} /> Accept
          </button>
        </div>
      )}
      {status === "proposed" && isProposer && (
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 text-center py-3 border border-dashed border-white/10 rounded-xl">
          Waiting for {partner?.name} to respond…
        </div>
      )}

      {/* Match link back */}
      <Link
        to={`/app/chat/${st.match_id}`}
        className="text-center text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#C6FF00] font-semibold py-2"
      >
        ← Back to chat
      </Link>
    </div>
  );
};

// Renders the right-side controls for one set, picking the right buttons based on `mode`.
const SetRowControls = ({ mode, index, s, acting, onMark, onConfirm }) => {
  if (mode === "locked") {
    return (
      <span className="px-2 py-1 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
        <Check size={10} /> Locked
      </span>
    );
  }
  if (mode === "mark") {
    return (
      <>
        <button
          onClick={() => onMark("completed")}
          disabled={acting}
          data-testid={`set-mark-completed-${index}`}
          className="w-full px-3 py-2 rounded-md bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider"
        >
          Completed
        </button>
        <button
          onClick={() => onMark("failed")}
          disabled={acting}
          data-testid={`set-mark-failed-${index}`}
          className="w-full px-3 py-2 rounded-md border border-white/15 text-neutral-300 text-[10px] font-bold uppercase tracking-wider"
        >
          Not Done
        </button>
      </>
    );
  }
  if (mode === "confirm") {
    return (
      <>
        <span className="text-[9px] uppercase tracking-wider text-neutral-400 mb-1">
          Partner says: {s.marked_status}
        </span>
        <button
          onClick={() => onConfirm("confirmed")}
          disabled={acting}
          data-testid={`set-confirm-${index}`}
          className="w-full px-3 py-2 rounded-md bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider"
        >
          Confirm
        </button>
        <button
          onClick={() => onConfirm("disputed")}
          disabled={acting}
          data-testid={`set-dispute-${index}`}
          className="w-full px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-[10px] font-bold uppercase tracking-wider"
        >
          Dispute
        </button>
      </>
    );
  }
  if (mode === "waiting") {
    return (
      <span className="px-2 py-1 rounded-full border border-white/15 text-[9px] uppercase tracking-wider text-neutral-400">
        Waiting partner
      </span>
    );
  }
  return (
    <span className="px-2 py-1 rounded-full border border-white/10 text-[9px] uppercase tracking-wider text-neutral-500">
      Pending
    </span>
  );
};

export default SetTestLive;
